import Errors from './Errors';

export default class {
    /**
     * Create a new Form instance.
     *
     * @param {object} data
     */
    constructor(data) {
        this.originalData = data;

        for (let field in data) {
            this[field] = data[field];
        }

        this.errors = new Errors();
    }

    /**
     * Fetch all relevant data for the form.
     */
    data() {
        let data = {};

        for (let property in this.originalData) {
            data[property] = this[property];
        }

        return data;
    }

    /**
     * Reset the form fields.
     */
    reset() {
        for (let field in this.originalData) {
            this[field] = this.originalData[field];
        }

        this.errors.clear();
    }

    /**
     * Send a POST request to the given URL.
     * .
     * @param {string} url
     * @param {object|null} payload what to send instead of the form's own fields
     */
    post(url, payload = null) {
        return this.submit('post', url, payload);
    }

    /**
     * Send a PUT request to the given URL.
     * .
     * @param {string} url
     * @param {object|null} payload what to send instead of the form's own fields
     */
    put(url, payload = null) {
        return this.submit('put', url, payload);
    }

    /**
     * Send a PATCH request to the given URL.
     * .
     * @param {string} url
     */
    patch(url) {
        return this.submit('patch', url);
    }

    /**
     * Send a DELETE request to the given URL.
     * .
     * @param {string} url
     */
    delete(url) {
        return this.submit('delete', url);
    }

    /**
     * Submit the form.
     *
     * A `payload` is for the forms whose screen shape is not their API shape —
     * a line editor holding a picked product, say. Errors still come back keyed
     * on the payload's field names, which is what the fields are named here.
     *
     * @param {string} requestType
     * @param {string} url
     * @param {object|null} payload what to send instead of the form's own fields
     */
    submit(requestType, url, payload = null) {
        return new Promise((resolve, reject) => {
            axios[requestType](url, payload ?? this.data())
                .then(response => {
                    this.onSuccess(response.data);
                    resolve(response);
                })
                .catch(error => {
                    // A failed submit must always settle this promise: the caller's
                    // `finally` is what releases the form's in-flight guard and its
                    // loading state. Reporting the error is done inside a try so that
                    // nothing thrown there (a network failure leaves no `response` to
                    // read) can leave the promise pending — that would lock the form
                    // for good, with no way to retry but closing it.
                    try {
                        const errors = error.response?.data?.errors;

                        if (errors) {
                            this.onFail(errors);
                        } else {
                            new AxiosError(error.response).manage();
                        }
                    } finally {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Handle a successful form submission.
     *
     * @param {object} data
     */
    onSuccess(data) {
        this.reset();
    }

    /**
     * Handle a failed form submission.
     *
     * @param {object} errors
     */
    onFail(errors) {
        this.errors.record(errors);
    }
}
